import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/hooks/useAuth";
import { MarkdownArticle } from "@/components/legal/MarkdownArticle";
import { toast } from "sonner";
import { StateLicensesPanel } from "@/components/admin/StateLicensesPanel";
import { CarrierDisclosuresPanel } from "@/components/admin/CarrierDisclosuresPanel";

export const Route = createFileRoute("/admin/legal")({
  head: () => ({
    meta: [
      { title: "Legal CMS — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Legal CMS" nav={ADMIN_NAV}>
        <LegalAdmin />
      </PortalShell>
    </ProtectedRoute>
  ),
});

type LegalPage = {
  id: string;
  slug: "privacy" | "terms" | "accessibility" | "disclosures";
  title: string;
  body: string;
  version: string;
  effective_date: string;
  status: "draft" | "published" | "archived";
  last_reviewed_at: string | null;
};

type Version = {
  id: string;
  version: string;
  title: string;
  body: string;
  status_at_save: string;
  changed_at: string;
  changed_by: string | null;
};

type Consent = {
  id: string;
  session_id: string;
  user_id: string | null;
  consent_analytics: boolean;
  consent_marketing: boolean;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
};

function LegalAdmin() {
  return (
    <div className="px-6 py-8 lg:px-10">
      <h1 className="font-serif text-3xl font-bold text-navy-900">Legal</h1>
      <p className="mt-2 font-sans text-sm text-charcoal-500">
        Edit policies, publish new versions, and review the cookie consent log.
      </p>

      <Tabs defaultValue="pages" className="mt-8">
        <TabsList>
          <TabsTrigger value="pages">Legal Pages</TabsTrigger>
          <TabsTrigger value="states">State Licenses</TabsTrigger>
          <TabsTrigger value="carriers">Carrier Disclosures</TabsTrigger>
          <TabsTrigger value="consent">Cookie Consent Log</TabsTrigger>
        </TabsList>
        <TabsContent value="pages" className="mt-6">
          <PagesPanel />
        </TabsContent>
        <TabsContent value="states" className="mt-6">
          <StateLicensesPanel />
        </TabsContent>
        <TabsContent value="carriers" className="mt-6">
          <CarrierDisclosuresPanel />
        </TabsContent>
        <TabsContent value="consent" className="mt-6">
          <ConsentLogPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PagesPanel() {
  const { user } = useAuth();
  const [pages, setPages] = useState<LegalPage[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LegalPage | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const { data } = await supabase
      .from("legal_pages")
      .select("*")
      .order("slug");
    if (data) {
      setPages(data as LegalPage[]);
      if (!activeId && data[0]) setActiveId((data[0] as LegalPage).id);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = pages.find((x) => x.id === activeId) ?? null;
    setDraft(p ? { ...p } : null);
    if (p) void loadVersions(p.id);
  }, [activeId, pages]);

  async function loadVersions(pageId: string) {
    const { data } = await supabase
      .from("legal_page_versions")
      .select("*")
      .eq("legal_page_id", pageId)
      .order("changed_at", { ascending: false })
      .limit(20);
    if (data) setVersions(data as Version[]);
  }

  async function snapshotVersion(p: LegalPage) {
    await supabase.from("legal_page_versions").insert({
      legal_page_id: p.id,
      slug: p.slug,
      title: p.title,
      body: p.body,
      version: p.version,
      status_at_save: p.status,
      changed_by: user?.id ?? null,
    });
  }

  async function handleSave(nextStatus?: LegalPage["status"]) {
    if (!draft) return;
    setSaving(true);
    const status = nextStatus ?? draft.status;
    const updates: Partial<LegalPage> & { last_reviewed_at?: string | null; last_reviewed_by?: string | null } = {
      title: draft.title,
      body: draft.body,
      version: draft.version,
      effective_date: draft.effective_date,
      status,
    };
    if (nextStatus === "published") {
      updates.last_reviewed_at = new Date().toISOString();
      updates.last_reviewed_by = user?.id ?? null;
    }
    const { data, error } = await supabase
      .from("legal_pages")
      .update(updates)
      .eq("id", draft.id)
      .select("*")
      .single();
    if (error || !data) {
      toast.error(error?.message || "Save failed.");
      setSaving(false);
      return;
    }
    await snapshotVersion(data as LegalPage);
    if (nextStatus === "published") {
      await supabase.from("activity_logs").insert({
        user_id: user?.id ?? null,
        action: "legal_page_published",
        target_type: "legal_page",
        target_id: draft.id,
        metadata: { slug: draft.slug, version: draft.version },
      });
      toast.success("Published.");
    } else if (nextStatus === "archived") {
      toast.success("Archived.");
    } else {
      toast.success("Draft saved.");
    }
    setSaving(false);
    await refresh();
    await loadVersions(draft.id);
  }

  function restoreVersion(v: Version) {
    if (!draft) return;
    setDraft({ ...draft, title: v.title, body: v.body, version: v.version });
    toast.success("Restored to editor. Save to commit.");
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
      {/* Page list */}
      <aside className="space-y-2 border border-bronze-600/30 bg-white p-4">
        {pages.map((p) => (
          <button
            key={p.id}
            onClick={() => setActiveId(p.id)}
            className={
              "flex w-full flex-col items-start gap-1 border-l-2 px-3 py-2 text-left transition-colors " +
              (activeId === p.id
                ? "border-bronze-600 bg-cream-50"
                : "border-transparent hover:bg-cream-50")
            }
          >
            <span className="font-sans text-sm font-semibold capitalize text-navy-900">
              {p.slug}
            </span>
            <span className="flex items-center gap-2">
              <StatusPill status={p.status} />
              <span className="font-sans text-[11px] text-charcoal-500">
                v{p.version}
              </span>
            </span>
          </button>
        ))}
      </aside>

      {/* Editor */}
      {draft ? (
        <div className="space-y-6 border border-bronze-600/30 bg-white p-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-3">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={draft.version}
                onChange={(e) => setDraft({ ...draft, version: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="effective_date">Effective date</Label>
              <Input
                id="effective_date"
                type="date"
                value={draft.effective_date}
                onChange={(e) =>
                  setDraft({ ...draft, effective_date: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Status</Label>
              <div className="mt-2">
                <StatusPill status={draft.status} />
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label htmlFor="body">Body (Markdown)</Label>
              <button
                onClick={() => setPreview((p) => !p)}
                className="font-sans text-xs uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800"
              >
                {preview ? "Edit" : "Preview"}
              </button>
            </div>
            {preview ? (
              <div className="min-h-[400px] border border-bronze-600/30 bg-cream-50 p-6">
                <MarkdownArticle body={draft.body} />
              </div>
            ) : (
              <Textarea
                id="body"
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                className="min-h-[480px] font-mono text-sm"
              />
            )}
          </div>

          <div className="flex flex-wrap gap-3 border-t border-bronze-600/30 pt-6">
            <PrimaryButton disabled={saving} onClick={() => handleSave("draft")}>
              Save draft
            </PrimaryButton>
            <SecondaryButton
              disabled={saving}
              onClick={() => handleSave("published")}
              className="border-navy-800 !text-navy-800 hover:!bg-navy-800 hover:!text-cream-50"
            >
              Publish
            </SecondaryButton>
            <button
              disabled={saving}
              onClick={() => handleSave("archived")}
              className="font-sans text-xs uppercase tracking-[0.18em] text-charcoal-500 hover:text-danger"
            >
              Archive
            </button>
          </div>

          <Accordion type="single" collapsible className="border-t border-bronze-600/30 pt-6">
            <AccordionItem value="history">
              <AccordionTrigger>Version history ({versions.length})</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-3">
                  {versions.map((v) => (
                    <li
                      key={v.id}
                      className="flex items-start justify-between gap-4 border border-bronze-600/20 bg-cream-50 p-3"
                    >
                      <div>
                        <p className="font-sans text-sm font-semibold text-navy-900">
                          v{v.version}{" "}
                          <span className="ml-2 font-normal text-charcoal-500">
                            {new Date(v.changed_at).toLocaleString()}
                          </span>
                        </p>
                        <p className="mt-1 font-sans text-xs text-charcoal-500">
                          Saved as <em>{v.status_at_save}</em>
                        </p>
                      </div>
                      <button
                        onClick={() => restoreVersion(v)}
                        className="font-sans text-xs uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800"
                      >
                        Restore →
                      </button>
                    </li>
                  ))}
                  {versions.length === 0 && (
                    <li className="font-sans text-sm text-charcoal-500">
                      No prior versions.
                    </li>
                  )}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      ) : (
        <div className="flex min-h-[400px] items-center justify-center border border-bronze-600/30 bg-white">
          <p className="font-sans text-sm text-charcoal-500">
            Select a page to edit.
          </p>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: LegalPage["status"] }) {
  const styles =
    status === "published"
      ? "bg-success/15 text-success border-success/30"
      : status === "draft"
      ? "bg-bronze-400/20 text-bronze-600 border-bronze-600/40"
      : "bg-muted text-charcoal-500 border-bronze-600/20";
  return (
    <span
      className={
        "inline-flex items-center border px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.18em] " +
        styles
      }
    >
      {status}
    </span>
  );
}

function ConsentLogPanel() {
  const [rows, setRows] = useState<Consent[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [onlyAnalytics, setOnlyAnalytics] = useState(false);
  const [onlyMarketing, setOnlyMarketing] = useState(false);
  const [scope, setScope] = useState<"all" | "user" | "anon">("all");

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, onlyAnalytics, onlyMarketing, scope]);

  async function load() {
    let q = supabase
      .from("cookie_consents")
      .select("id, session_id, user_id, consent_analytics, consent_marketing, ip_address, user_agent, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (from) q = q.gte("created_at", from);
    if (to) q = q.lte("created_at", to);
    if (onlyAnalytics) q = q.eq("consent_analytics", true);
    if (onlyMarketing) q = q.eq("consent_marketing", true);
    if (scope === "user") q = q.not("user_id", "is", null);
    if (scope === "anon") q = q.is("user_id", null);
    const { data } = await q;
    if (data) setRows(data as Consent[]);
  }

  const csv = useMemo(() => {
    const head = "timestamp,session_id,user_id,analytics,marketing,ip,user_agent\n";
    const body = rows
      .map((r) =>
        [
          r.created_at,
          r.session_id,
          r.user_id ?? "",
          r.consent_analytics,
          r.consent_marketing,
          r.ip_address ?? "",
          (r.user_agent ?? "").replace(/,/g, " "),
        ].join(","),
      )
      .join("\n");
    return head + body;
  }, [rows]);

  function exportCsv() {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cookie-consents-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 border border-bronze-600/30 bg-white p-4">
        <div>
          <Label>From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 font-sans text-sm">
          <input type="checkbox" checked={onlyAnalytics} onChange={(e) => setOnlyAnalytics(e.target.checked)} />
          Analytics only
        </label>
        <label className="flex items-center gap-2 font-sans text-sm">
          <input type="checkbox" checked={onlyMarketing} onChange={(e) => setOnlyMarketing(e.target.checked)} />
          Marketing only
        </label>
        <div>
          <Label>Scope</Label>
          <select
            className="h-10 border border-input bg-background px-3 font-sans text-sm"
            value={scope}
            onChange={(e) => setScope(e.target.value as "all" | "user" | "anon")}
          >
            <option value="all">All</option>
            <option value="user">Signed in</option>
            <option value="anon">Anonymous</option>
          </select>
        </div>
        <div className="ml-auto">
          <PrimaryButton onClick={exportCsv}>Export CSV</PrimaryButton>
        </div>
      </div>

      <div className="overflow-x-auto border border-bronze-600/30 bg-white">
        <table className="w-full font-sans text-sm">
          <thead className="bg-cream-50 text-left text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
            <tr>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Session</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Analytics</th>
              <th className="px-4 py-3">Marketing</th>
              <th className="px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-bronze-600/20">
                <td className="px-4 py-3 text-charcoal-900">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-charcoal-500">
                  {r.session_id.slice(0, 12)}…
                </td>
                <td className="px-4 py-3 font-mono text-xs text-charcoal-500">
                  {r.user_id ? r.user_id.slice(0, 8) + "…" : "—"}
                </td>
                <td className="px-4 py-3">{r.consent_analytics ? "✓" : "—"}</td>
                <td className="px-4 py-3">{r.consent_marketing ? "✓" : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-charcoal-500">
                  {r.ip_address ?? "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-charcoal-500">
                  No consent records match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}